import { type ReactNode, useCallback, useRef, useState } from 'react';
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';

export interface ConfirmOptions {
  title: string;
  /** Optional supporting copy under the title (e.g. "This can't be undone."). */
  description?: ReactNode;
  /** Confirm button label. Default "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Default "Cancel". */
  cancelLabel?: string;
  /** Confirm button style. Default "danger" (most confirms are destructive). */
  variant?: 'danger' | 'primary';
}

/**
 * Promise-based confirmation dialog — a drop-in replacement for the
 * native `window.confirm`. Keeps the `if (!(await confirm(...))) return;`
 * control flow callers already had, but renders a branded modal instead
 * of the browser's chrome.
 *
 * Usage:
 *   const { confirm, confirmDialog } = useConfirm();
 *   ...
 *   onClick={async () => {
 *     if (!(await confirm({ title: 'Delete X?', description: '…' }))) return;
 *     await doTheThing();
 *   }}
 *   ...
 *   return (<>{…}{confirmDialog}</>);
 *
 * The dialog closes the moment the user picks an option and the promise
 * resolves — the caller's own loading state (button `disabled`, toast)
 * covers the work that follows, exactly as it did with native confirm.
 * Only one confirmation can be pending at a time (it's modal).
 */
export function useConfirm(): {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  confirmDialog: JSX.Element;
} {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    setOpts(options);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOpen(false);
  }, []);

  const confirmDialog = (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Escape / overlay click / X all resolve as "cancelled".
        if (!o) settle(false);
      }}
    >
      {/* When there's no description, opt out of Radix's
          aria-describedby auto-wiring to silence its dev warning;
          when there is one, let Radix link it automatically. */}
      <DialogContent size="sm" {...(opts?.description ? {} : { 'aria-describedby': undefined })}>
        <DialogHeader>
          <DialogTitle>{opts?.title ?? 'Are you sure?'}</DialogTitle>
          {opts?.description ? <DialogDescription>{opts.description}</DialogDescription> : null}
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => settle(false)}>
            {opts?.cancelLabel ?? 'Cancel'}
          </Button>
          <Button variant={opts?.variant ?? 'danger'} onClick={() => settle(true)}>
            {opts?.confirmLabel ?? 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { confirm, confirmDialog };
}
